import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import { Outlet, generatePath, useOutletContext } from 'react-router-dom';

import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { NewPageHeader } from '@sorare/core/src/components/navigation/NewPageHeader';
import { PageHeader as OldPageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { Tabs, TeamPageTabs } from '@sorare/core/src/components/seo/Team/Tabs';
import {
  FOOTBALL_CLUB_SHOW,
  FOOTBALL_CLUB_SHOW_CARDS,
  FOOTBALL_CLUB_SHOW_FIXTURES,
  FOOTBALL_CLUB_SHOW_STANDINGS,
  FOOTBALL_CLUB_SHOW_TEAM,
} from '@sorare/core/src/constants/routes';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { withRouteQuery } from '@sorare/core/src/lib/navigation/withRouteQuery';

import {
  FootballSeoClubQuery,
  FootballSeoClubQueryVariables,
} from './__generated__/layout.graphql';
import { PageParams } from './__generated__/routeParams';

const FOOTBALL_SEO_CLUB_QUERY = gql`
  query FootballSeoClubQuery($slug: String!) {
    football {
      club(slug: $slug) {
        slug
        name
        domesticLeague {
          slug
          seasons {
            id
            startYear
          }
        }
        latestGames(first: 1) {
          nodes {
            id
          }
        }
      }
    }
  }
` as TypedDocumentNode<FootballSeoClubQuery, FootballSeoClubQueryVariables>;

type LayoutData = FootballSeoClubQuery['football']['club'];

export const useLayoutData = () => {
  return useOutletContext<LayoutData>();
};

export default withRouteQuery(
  ({ queryResult }) => {
    const {
      flags: { useNewNav = false },
    } = useFeatureFlags();

    const club = queryResult.data?.football.club;
    const slug = club?.slug;

    const tabs: Record<TeamPageTabs, string> = useMemo(() => {
      if (!slug) return {} as Record<TeamPageTabs, string>;

      return {
        [TeamPageTabs.OVERVIEW]: generatePath(FOOTBALL_CLUB_SHOW, {
          slug,
        }),
        [TeamPageTabs.STANDINGS]: generatePath(FOOTBALL_CLUB_SHOW_STANDINGS, {
          slug,
        }),
        [TeamPageTabs.FIXTURES]: generatePath(FOOTBALL_CLUB_SHOW_FIXTURES, {
          slug,
        }),
        [TeamPageTabs.TEAM]: generatePath(FOOTBALL_CLUB_SHOW_TEAM, {
          slug,
        }),
        [TeamPageTabs.CARDS]: generatePath(FOOTBALL_CLUB_SHOW_CARDS, {
          slug,
        }),
      };
    }, [slug]);

    if (!slug || !club) return null;

    const hasEnoughSEOData =
      !!club.domesticLeague || club.latestGames.nodes.length > 0;

    const PageHeader = useNewNav ? NewPageHeader : OldPageHeader;
    return (
      <>
        <PageHeader text={club.name} />
        {!hasEnoughSEOData && <NoIndex />}
        <ContentWithStickyHeader header={<Tabs tabs={tabs} />}>
          <Outlet context={club satisfies LayoutData} />
        </ContentWithStickyHeader>
      </>
    );
  },
  {
    query: FOOTBALL_SEO_CLUB_QUERY,
    queryOptions: ({ slug }: PageParams) => ({ variables: { slug } }),
  }
);
